/**
 * Mounts this panel's guided tours inside the router, wiring the shared
 * engine to the signed-in person's roles, the current route and the server
 * (client.retail.tours -> /v3/me/tours). Bill 2026-10-07.
 */
import { useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { GuidedTourProvider, type TourStore } from '@retail-ops/guided-tour';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { TOURS } from './riskTours';

export function PanelGuidedTours({ children }: { children: ReactNode }): JSX.Element {
  const { user } = useAuth();
  const client = useApiClient();
  const navigate = useNavigate();
  const loc = useLocation();
  const roles = useMemo(() => (user ? user.roles.map(String) : null), [user]);
  const store = useMemo<TourStore>(() => ({
    load: async () => {
      const r = await client.retail.tours.mine();
      return { enabled: r.data.enabled, progress: r.data.progress };
    },
    record: (tourId, version, status, lastStep) => client.retail.tours.record(tourId, { version, status, lastStep }),
  }), [client]);
  return (
    <GuidedTourProvider tours={TOURS} roles={roles} store={store} navigate={navigate} currentPath={loc.pathname}>
      {children}
    </GuidedTourProvider>
  );
}

export default PanelGuidedTours;
